import {
  encodeFunctionData,
  toHex,
  type Abi,
  type Address,
  type Hash,
  type Hex,
} from 'viem'
import { writeContract } from 'wagmi/actions'
import type { Connector } from 'wagmi'

import { wagmiConfig } from '../../wagmi/config'
import { tMsg } from '@/features/locale/translate'
import { asChainId } from '../../sdk/chainId'
import { txSubmitDebug, txSubmitWarn } from '../../utils/debugTxSubmit'
import { ensureAppChainInWallet, mapEnsureChainError } from './ensureAppChainInWallet'
import { getConnectorChainId } from './getConnectorChainId'
import { isWalletUserRejection } from './isWalletUserRejection'
import { getAppSigningChain, resolveWalletClient } from './resolveWalletClient'

type EIP1193Provider = {
  request(args: { method: string; params?: unknown[] | object }): Promise<unknown>
}

export type ContractWriteParams = {
  address: Hash
  abi: Abi
  functionName: string
  args: readonly unknown[]
  account: Address
  chainId: number
  connector: Connector
  gas?: bigint
  value?: bigint
}

async function getSigningProvider(connector: Connector, chainId: number): Promise<EIP1193Provider> {
  try {
    return (await connector.getProvider({ chainId })) as EIP1193Provider
  } catch {
    /* MetaMask móvil a veces falla con chainId explícito en redes custom */
  }

  try {
    return (await connector.getProvider()) as EIP1193Provider
  } catch {
    /* fallback below */
  }

  if (typeof window !== 'undefined' && window.ethereum) {
    return window.ethereum as EIP1193Provider
  }

  throw new Error('provider unavailable')
}

async function sendViaWalletClient(params: ContractWriteParams): Promise<Hash> {
  const { address, abi, functionName, args, account, chainId, connector, gas, value } = params
  const walletClient = await resolveWalletClient(connector, undefined, chainId, account)
  if (!walletClient) {
    throw new Error('wallet client unavailable')
  }

  return walletClient.writeContract({
    address,
    abi,
    functionName,
    args: args as never,
    account,
    chain: getAppSigningChain(chainId),
    gas,
    value,
  } as Parameters<typeof walletClient.writeContract>[0])
}

async function sendViaProvider(params: ContractWriteParams): Promise<Hash> {
  const { address, abi, functionName, args, account, chainId, connector, gas, value } = params
  const provider = await getSigningProvider(connector, chainId)
  const data = encodeFunctionData({
    abi,
    functionName,
    args: args as never,
  })

  const tx: Record<string, Hex> = {
    from: account,
    to: address,
    data,
  }
  if (gas) tx.gas = toHex(gas)
  if (value) tx.value = toHex(value)

  const hash = await provider.request({
    method: 'eth_sendTransaction',
    params: [tx],
  })

  return hash as Hash
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'shortMessage' in error) {
    return String((error as { shortMessage?: string }).shortMessage)
  }
  return String(error)
}

export function mapWalletWriteError(...errors: unknown[]): Error {
  const rejection = errors.find(isWalletUserRejection)
  if (rejection) {
    return new Error(tMsg().wallet.error.userRejected)
  }

  const combined = errors.map(errorMessage).join(' ').toLowerCase()
  const { wallet } = tMsg()

  if (
    combined.includes('user rejected') ||
    combined.includes('user denied') ||
    combined.includes('rejected the request')
  ) {
    return new Error(wallet.error.userRejected)
  }

  if (combined.includes('chain mismatch') || combined.includes('wrong network')) {
    return new Error(wallet.error.chainMismatch)
  }

  if (
    combined.includes('not connected') ||
    combined.includes('connectornotconnected') ||
    combined.includes('provider unavailable') ||
    combined.includes('wallet client unavailable')
  ) {
    return new Error(wallet.error.metamaskNoResponse)
  }

  if (combined.includes('no chain was provided')) {
    return new Error(wallet.error.chainPrepareFailed)
  }

  return new Error(wallet.error.txSendFailed)
}

/** Envía una transacción de contrato con fallbacks para MetaMask móvil. */
export async function submitContractWrite(params: ContractWriteParams): Promise<Hash> {
  const walletChainId = await getConnectorChainId(params.connector)
  if (walletChainId !== params.chainId) {
    try {
      await ensureAppChainInWallet(params.connector)
    } catch (error) {
      throw mapEnsureChainError(error)
    }
  }

  const { value, ...rest } = params
  const writeParams = {
    ...rest,
    ...(value ? { value } : {}),
  }

  try {
    return await writeContract(wagmiConfig, {
      ...writeParams,
      chainId: asChainId(params.chainId),
    })
  } catch (primaryError) {
    if (isWalletUserRejection(primaryError)) {
      throw mapWalletWriteError(primaryError)
    }

    txSubmitWarn('writeContract primary failed', { message: errorMessage(primaryError) })

    try {
      txSubmitDebug('submitContractWrite fallback: walletClient', { chainId: params.chainId })
      return await sendViaWalletClient(params)
    } catch (secondaryError) {
      if (isWalletUserRejection(secondaryError)) {
        throw mapWalletWriteError(primaryError, secondaryError)
      }

      txSubmitWarn('walletClient fallback failed', { message: errorMessage(secondaryError) })

      try {
        txSubmitDebug('submitContractWrite fallback: eth_sendTransaction', { chainId: params.chainId })
        return await sendViaProvider(params)
      } catch (tertiaryError) {
        txSubmitWarn('provider fallback failed', { message: errorMessage(tertiaryError) })
        throw mapWalletWriteError(primaryError, secondaryError, tertiaryError)
      }
    }
  }
}
